import React, {useRef, useEffect, useCallback, useState} from 'react';
import { findNearestConnectionPoint, createThemedElementFactory } from '../../types/elements';
import Editor from '@monaco-editor/react';
import { CODE_SAMPLES } from '../../codeSamples';
import { useTheme } from '../../hooks/useTheme';
import { setupMonacoEditor, updateRegistryEntities, updateStructurizrRegistryEntities, setActiveMetamodelContext } from '../../utils/monacoSetup';
import { lintStructurizrDsl } from '../../utils/structurizrLinter';
import { parsePlantUml, isPlantUmlCode } from '../../utils/plantumlParser';
import { parseErdDsl, isErdDsl } from '../../utils/erdParser';
import { parseArchimatePlantUml, isArchimatePlantUmlCode } from '../../utils/archimateParser';
import { parseStructurizrDsl, isStructurizrDslCode } from '../../utils/structurizrParser';
import { parseFreeDsl, isFreeDslCode } from '../../utils/freeDslParser';
import { layoutFreeDsl } from '../../utils/freeDslLayout';
import { lintFreeDsl } from '../../utils/freeDslLinter';
import * as plantumlLinter from '../../utils/plantumlLinter';
import { listSystems, getAllEntityCodes, getEntitiesMap } from '../../api/systemsApi';
import { bboxOf, shift, snapArrows, centerTexts, autosizeShapes } from '../../utils/elementUtils';
import { VIRTUAL_SIZE } from '../../utils/canvasUtils';
import { createCodeHelpers } from '../../utils/codeHelpers';
import { analyzeWithDeepSeek } from '../../api/deepseekApi';
import {
    CollapsedToggle,
    Panel,
    PanelHeader,
    Title,
    IconButton,
    EditorWrapper,
    ErrorBox,
    AnalysisWrapper,
    AnalysisHeader,
    AnalysisTitle,
    HideAnalysisBtn,
    Spinner,
    AnalyzingBox,
    AnalysisContent,
    AnalysisText,
    AnalysisFooter,
    AnalysisError,
    LintWrapper,
    LintHeader,
    LintTitle,
    LintList,
    LintItem,
    LintItemHeader,
    LintBadge,
    LintMeta,
    LintActions,
    LintButton
} from './CodeGenerator.styles';

/* eslint-disable no-template-curly-in-string */
/* eslint-disable no-new-func */

// Изменение здесь: извлекаем нужные функции и константы
const { lintPlantUml, Severity } = plantumlLinter;

const CodeGenerator = ({ onGenerateElements, existingElements = [], viewportCenter = { x: 0, y: 0 }, topOffset = 0, hasToolbar = true, fitBBoxInView, code, setCode, dslText, onElementMapChange, onLintDiagnosticsChange, onRegistryContextChange, onUserCodeChange, diagramMode = '', metamodelContext = null, defaultCode = null }) => {
    const { theme } = useTheme();
    const builtinSample = diagramMode === 'free'
        ? CODE_SAMPLES.freeDsl
        : diagramMode === 'c4'
        ? CODE_SAMPLES.structurizrC4
        : diagramMode === 'erd'
        ? CODE_SAMPLES.erdDefault
        : CODE_SAMPLES.plantUmlSequence;
    // defaultCode (from metamodel) takes precedence over the built-in sample
    const defaultSample = defaultCode || builtinSample;
    const defaultRef = useRef(defaultSample);
    const [error, setError] = useState('');
    const [collapsed, setCollapsed] = useState(false);
    const [wide, setWide] = useState(false);
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const autogenDoneRef = useRef(false);

    // Push externally-generated DSL into the Monaco editor
    useEffect(() => {
        if (!dslText || !editorRef.current) return;
        editorRef.current.setValue(dslText);
    }, [dslText]);

    useEffect(() => {
        if (diagramMode !== 'c4' && diagramMode !== 'free') return;
        if (code && code.trim()) return;
        setCode(defaultSample);
    }, [code, defaultSample, diagramMode, setCode]);

    // Sync active metamodel context into Monaco tokenizer/autocomplete
    useEffect(() => {
        setActiveMetamodelContext(metamodelContext);
    }, [metamodelContext]);

    // Линтер
    const [lintDiagnostics, setLintDiagnostics] = useState([]);
    const [showLint, setShowLint] = useState(true);
    const lintTimerRef = useRef(null);

    useEffect(() => {
        onLintDiagnosticsChange?.(lintDiagnostics);
    }, [lintDiagnostics, onLintDiagnosticsChange]);

    // Разрешённые участники из реестра ИС
    const [allowedParticipants, setAllowedParticipants] = useState([]); // только коды ИС
    const codeToNameRef = useRef(new Map());

    // DeepSeek анализ
    const [analysis, setAnalysis] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [showAnalysis, setShowAnalysis] = useState(false);

    // Тематизированный ElementFactory
    const ThemedElementFactory = createThemedElementFactory(theme);
    const Helpers = createCodeHelpers(ThemedElementFactory, findNearestConnectionPoint);
    const decorationsRef = useRef([]);

    // ---------------- ЛОГИКА ПОСТОБРАБОТКИ ----------------
    const processAndSendElements = useCallback((elements) => {
        const sized = autosizeShapes(elements);
        const snapped = snapArrows(sized, existingElements, findNearestConnectionPoint);
        // Применяем подсветку по линтеру на этапе генерации
        let toDecorate = snapped;
        try {
            const shouldDecorateWithPlantUmlLint = diagramMode !== 'erd' && isPlantUmlCode(code);
            const diags = shouldDecorateWithPlantUmlLint ? (lintPlantUml(code, { allowedParticipants }) || []) : [];
            const missing = diags.filter(d => d.ruleId === 'missing-reply');
            if (missing.length) {
                toDecorate = snapped.map(el => {
                    if (el.type === 'arrow' && el.isMessage && el.fromActor && el.toActor) {
                        const hit = missing.find(m => m.from === el.fromActor && m.to === el.toActor);
                        if (hit) {
                            return { ...el, lintHighlight: 'warning' };
                        }
                    }
                    return el;
                });
            }
        } catch {}
        const withTexts = centerTexts(toDecorate, existingElements);

        // Размещаем элементы в центре виртуального холста
        // fitBBoxInView затем автоматически отцентрирует viewport на них
        const bb = bboxOf(withTexts);
        let placed = withTexts;
        if (bb && isFinite(bb.cx) && isFinite(bb.cy)) {
            // Всегда размещаем в центре VIRTUAL_SIZE для предсказуемости
            const targetCenter = { x: VIRTUAL_SIZE / 2, y: VIRTUAL_SIZE / 2 };
            const dx = targetCenter.x - bb.cx;
            const dy = targetCenter.y - bb.cy;
            placed = shift(withTexts, dx, dy);
        }

        onGenerateElements(placed);

        // Логирование для отладки
        console.log('Генерация завершена, элементов:', placed.length);

        if (fitBBoxInView) {
            const finalBB = bboxOf(placed);
            if (finalBB) {
                // Небольшой таймаут чтобы убедиться что DOM/State обновился и Canvas готов применить зум
                setTimeout(() => fitBBoxInView(finalBB), 100);
            }
        }
    }, [code, existingElements, fitBBoxInView, onGenerateElements, allowedParticipants, diagramMode]);

    // ---------------- ГЕНЕРАЦИЯ ----------------
    const generateElements = useCallback(() => {
        try {
            setError('');
            const raw = code || '';
            // C4 mode: only Structurizr DSL accepted
            if (diagramMode === 'c4') {
                if (!isStructurizrDslCode(raw)) {
                    setError('На C4-диаграммах поддерживается только Structurizr DSL. Начните с workspace {');
                    return;
                }
                const { elements: els, elementMap } = parseStructurizrDsl(raw, ThemedElementFactory);
                processAndSendElements(els);
                onElementMapChange?.(elementMap);
                return;
            }
            if (diagramMode === 'free' || isFreeDslCode(raw)) {
                const ast = parseFreeDsl(raw);
                const els = layoutFreeDsl(ast, ThemedElementFactory, metamodelContext);
                processAndSendElements(els);
                return;
            }
            // Non-C4: Structurizr DSL still accepted (existing behaviour)
            if (isStructurizrDslCode(raw)) {
                const { elements: els, elementMap } = parseStructurizrDsl(raw, ThemedElementFactory);
                processAndSendElements(els);
                onElementMapChange?.(elementMap);
                return;
            }
            // ERD (PlantUML class diagrams) — check before sequence PlantUML
            if (diagramMode === 'erd' || isErdDsl(raw)) {
                const els = parseErdDsl(raw, ThemedElementFactory);
                processAndSendElements(els);
                return;
            }
            // ArchiMate PlantUML — check before sequence diagrams (more specific)
            if (isArchimatePlantUmlCode(raw)) {
                const els = parseArchimatePlantUml(raw, ThemedElementFactory);
                processAndSendElements(els);
                return;
            }
            if (isPlantUmlCode(raw)) {
                // Создаем Map сущностей для передачи в парсер
                (async () => {
                    const entitiesMap = await getEntitiesMap();
                    const els = parsePlantUml(raw, ThemedElementFactory, { systemsMap: entitiesMap });
                    processAndSendElements(els);
                })();
                return;
            }
            const run = (user) => {
                const f = new Function('ElementFactory', 'findNearestConnectionPoint', 'existingElements', 'Helpers', user);
                return f(ThemedElementFactory, findNearestConnectionPoint, existingElements, Helpers);
            };
            const res = run(raw);
            if (!Array.isArray(res)) { setError('Код должен возвращать массив элементов'); return; }
            processAndSendElements(res);
        } catch (e) {
            setError('Ошибка: ' + e.message);
        }
    }, [code, existingElements, ThemedElementFactory, Helpers, processAndSendElements]);

    // Центрирование существующих элементов (кнопка)
    const centerExisting = () => {
        if (!existingElements.length) return;
        const bb = bboxOf(existingElements);
        if (!bb) return;
        const dx = viewportCenter.x - bb.cx;
        const dy = viewportCenter.y - bb.cy;
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
        onGenerateElements(shift(existingElements, dx, dy));
    };

    // ---------------- ЛИНТЕР ----------------
    useEffect(() => {
        if (diagramMode === 'free') {
            if (lintTimerRef.current) clearTimeout(lintTimerRef.current);
            const diags = lintFreeDsl(code, metamodelContext);
            setLintDiagnostics(diags);
            updateMonacoMarkers(diags);
            if (diags.length) setShowLint(true);
            return;
        }
        if (diagramMode === 'c4') {
            // Clear any pending PlantUML debounce timer (e.g. if mode was just switched)
            if (lintTimerRef.current) clearTimeout(lintTimerRef.current);
            // Structurizr linter — runs synchronously (DSL parsing is cheap)
            const allowedNames = [...codeToNameRef.current.values()];
            const diags = lintStructurizrDsl(code, { allowedCodes: allowedParticipants, allowedNames });
            setLintDiagnostics(diags);
            updateMonacoMarkers(diags);
            if (diags.length) setShowLint(true);
            return;
        }
        if (diagramMode === 'erd') {
            if (lintTimerRef.current) clearTimeout(lintTimerRef.current);
            setLintDiagnostics([]);
            updateMonacoMarkers([]);
            return;
        }
        // PlantUML linter — unchanged (300ms debounce)
        if (!isPlantUmlCode(code)) { setLintDiagnostics([]); updateMonacoMarkers([]); return; }
        if (typeof lintPlantUml !== 'function') { console.warn('plantumlLinter.lintPlantUml не доступен'); setLintDiagnostics([]); updateMonacoMarkers([]); return; }
        if (lintTimerRef.current) clearTimeout(lintTimerRef.current);
        lintTimerRef.current = setTimeout(() => {
            const diags = lintPlantUml(code, { allowedParticipants });
            setLintDiagnostics(diags);
            updateMonacoMarkers(diags);
            if (diags.length) setShowLint(true);
        }, 300);
        return () => lintTimerRef.current && clearTimeout(lintTimerRef.current);
    }, [code, allowedParticipants, diagramMode]);

    const updateMonacoMarkers = (diags) => {
        const ed = editorRef.current; const monaco = monacoRef.current; if (!ed || !monaco) return;
        const model = ed.getModel(); if (!model) return;
        const markers = diags.map(d => ({
            startLineNumber: d.line,
            endLineNumber: d.line,
            startColumn: 1,
            endColumn: model.getLineLength(d.line) + 1,
            message: d.message,
            severity: d.severity === Severity.ERROR ? monaco.MarkerSeverity.Error : d.severity === Severity.WARNING ? monaco.MarkerSeverity.Warning : monaco.MarkerSeverity.Info,
            source: 'plantuml-lint',
            code: d.ruleId
        }));
        monaco.editor.setModelMarkers(model, 'plantuml-lint', markers);

        // Inline decorations
        const decos = diags.map(d => ({
            range: new monaco.Range(d.line, 1, d.line, Math.max(1, model.getLineLength(d.line) + 1)),
            options: {
                isWholeLine: true,
                className: d.severity === Severity.ERROR ? 'lint-inline-error' : d.severity === Severity.WARNING ? 'lint-inline-warning' : 'lint-inline-info',
                inlineClassName: d.severity === Severity.ERROR ? 'lint-inline-error' : d.severity === Severity.WARNING ? 'lint-inline-warning' : 'lint-inline-info'
            }
        }));
        decorationsRef.current = ed.deltaDecorations(decorationsRef.current || [], decos);
    };

    const jumpToLine = (line) => {
        const ed = editorRef.current; if (!ed) return;
        ed.revealLineInCenter(line);
        ed.setPosition({ lineNumber: line, column: 1 });
        ed.focus();
    };

    const applySuggestion = (diag) => {
        if (!diag.suggestion) return;
        const ed = editorRef.current; const monaco = monacoRef.current; if (!ed || !monaco) return;
        const model = ed.getModel(); if (!model) return;
        const insertLine = diag.line + 1;
        model.pushEditOperations([], [{ range: new monaco.Range(insertLine, 1, insertLine, 1), text: `\n${diag.suggestion}` }], () => null);
    };

    // ---------------- DEEPSEEK АНАЛИЗ ----------------
    const analyzeWithDeepSeekAsync = async (diagramCode, elements) => {
        if (!diagramCode || !diagramCode.trim()) return;
        setIsAnalyzing(true);
        setAnalysis(null);
        try {
            const result = await analyzeWithDeepSeek(diagramCode, elements);
            setAnalysis(result);
            setShowAnalysis(true);
        } catch (e) {
            console.warn('DeepSeek анализ недоступен', e);
            setAnalysis({ success: false, error: 'DeepSeek анализ временно недоступен' });
            setShowAnalysis(true);
        } finally {
            setIsAnalyzing(false);
        }
    };

    // Есть ли ошибки по реестру ИС (free-mode не проверяем — у него нет реестровых участников)
    const hasRegistryErrors = diagramMode !== 'c4' && diagramMode !== 'erd' && diagramMode !== 'free' && lintDiagnostics.some(
        (d) => d.ruleId === 'registry-unknown-participant' && d.severity === Severity.ERROR
    );

    // ---------------- ЗАГРУЗКА РЕЕСТРА ИС ----------------
    useEffect(() => {
        (async () => {
            try {
                const [systemsList, entityCodes, entitiesMap] = await Promise.all([
                    listSystems(),
                    getAllEntityCodes(),
                    getEntitiesMap()
                ]);

                setAllowedParticipants(entityCodes); // теперь включает и системы, и модули

                const codeToName = new Map();
                entitiesMap.forEach((entity, code) => {
                    codeToName.set(code, entity.name);
                });
                codeToNameRef.current = codeToName;
                onRegistryContextChange?.({
                    allowedParticipants: entityCodes,
                    allowedNames: [...codeToName.values()],
                });

                // Обновляем автокомплит Monaco Editor
                updateRegistryEntities(entitiesMap);
                updateStructurizrRegistryEntities(entitiesMap);

                console.log('Загружено из реестра:', {
                    systems: systemsList.length,
                    totalEntities: entityCodes.length,
                    codes: entityCodes
                });
            } catch (e) {
                console.error('Не удалось загрузить реестр ИС', e);
            }
        })();
    }, [onRegistryContextChange]);

    // ---------------- АВТОГЕНЕРАЦИЯ ПРИ МОНТИРОВАНИИ ----------------
    useEffect(() => {
        if (autogenDoneRef.current) return;
        if (existingElements.length === 0 && code && code.trim()) {
            autogenDoneRef.current = true;
            const t = setTimeout(() => generateElements(), 500);
            return () => clearTimeout(t);
        }
    }, [code, existingElements.length, generateElements]);

    // ---------------- АВТОКОМПЛИТ НА ПУСТЫХ СТРОКАХ ----------------
    // Убрано по просьбе: подсказки не должны появляться сразу на пустой строке
    // useEffect(() => {
    //     const ed = editorRef.current;
    //     if (!ed) return;
    //     const model = ed.getModel();
    //     if (!model) return;
    //
    //     const disposable = ed.onDidChangeCursorPosition((e) => {
    //         const line = e.position.lineNumber;
    //         if (model.getLineContent(line).trim() === '') {
    //             ed.trigger('auto', 'editor.action.triggerSuggest', {});
    //         }
    //     });
    //
    //     return () => disposable.dispose();
    // }, [code]);

    // Replace code when the active default changes (mode switch or metamodel template arriving)
    useEffect(() => {
        if (defaultRef.current !== defaultSample) {
            if (!code || !code.trim() || code === defaultRef.current) setCode(defaultSample);
            defaultRef.current = defaultSample;
        }
    }, [code, defaultSample]);

    const language = diagramMode === 'free'
        ? 'free-dsl'
        : diagramMode === 'erd'
        ? 'plantuml'
        : isStructurizrDslCode(code)
        ? 'hcl'
        : isPlantUmlCode(code)
        ? 'plantuml'
        : 'javascript';

    const handleEditorChange = useCallback((value) => {
        const next = value || '';
        if (typeof onUserCodeChange === 'function') {
            onUserCodeChange(next);
        } else {
            setCode(next);
        }
    }, [onUserCodeChange, setCode]);

    // ---------------- UI ----------------
    if (collapsed) {
        return (
            <CollapsedToggle theme={theme} topOffset={topOffset} hasToolbar={hasToolbar} onClick={() => setCollapsed(false)} title="Показать редактор">📝</CollapsedToggle>
        );
    }

    return (
        <Panel theme={theme} wide={wide} hasToolbar={hasToolbar} topOffset={topOffset}>
            <PanelHeader theme={theme}>
                <Title theme={theme}>КОД</Title>
                <IconButton theme={theme} title={hasRegistryErrors ? 'Исправьте ошибки реестра ИС' : 'Сгенерировать'} onClick={() => {
                    // Дополнительная защита — пересчёт на лету
                    const diagsNow = diagramMode !== 'erd' && diagramMode !== 'free' && isPlantUmlCode(code) ? lintPlantUml(code, { allowedParticipants }) : [];
                    const block = diagsNow.some((d) => d.ruleId === 'registry-unknown-participant' && d.severity === Severity.ERROR);
                    if (block) {
                        setError('Нельзя сгенерировать: есть участники, отсутствующие в реестре ИС. Исправьте объявления participant/database.');
                        setShowLint(true);
                        return;
                    }
                    generateElements();
                }} disabled={hasRegistryErrors}>▶</IconButton>
                <IconButton theme={theme} title="Провести анализ ИИ" onClick={() => analyzeWithDeepSeekAsync(code, existingElements)} disabled={isAnalyzing || !code.trim()}>🤖</IconButton>
                <IconButton theme={theme} title="Центрировать" onClick={centerExisting}>◎</IconButton>
                <IconButton theme={theme} title={wide ? 'Узкая' : 'Широкая'} onClick={() => setWide(w => !w)}>⇔</IconButton>
                <IconButton theme={theme} title="Свернуть" onClick={() => setCollapsed(true)}>✕</IconButton>
                <IconButton theme={theme} title={showLint ? 'Скрыть предупреждения' : 'Показать предупреждения'} onClick={() => setShowLint(v => !v)}>⚠️</IconButton>
            </PanelHeader>
            <EditorWrapper>
                <Editor
                    value={code}
                    onChange={handleEditorChange}
                    language={language}
                    theme={theme.name === 'dark' ? 'custom-dark' : 'vs'}
                    options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        wordWrap: 'on',
                        scrollBeyondLastLine: false,
                        smoothScrolling: true,
                        automaticLayout: true,
                        padding: { top: 6 },
                        lineHeight: 20,
                        suggestOnTriggerCharacters: true,
                        quickSuggestions: true,
                        acceptSuggestionOnEnter: 'off',
                        tabSize: 2,
                        renderWhitespace: 'selection'
                    }}
                    beforeMount={(monaco) => { setupMonacoEditor(monaco); monacoRef.current = monaco; }}
                    onMount={(ed, monaco) => {
                        editorRef.current = ed;
                        monacoRef.current = monaco;
                        const current = ed.getValue();
                        if (!current || !current.trim()) {
                            const template = diagramMode === 'free'
                                ? CODE_SAMPLES.freeDsl
                                : diagramMode === 'c4'
                                ? CODE_SAMPLES.structurizrC4
                                : diagramMode === 'erd'
                                ? CODE_SAMPLES.erdDefault
                                : '@startuml\n\n@enduml';
                            ed.setValue(template);
                            setCode(template);
                            if (diagramMode !== 'c4') {
                                ed.setPosition({ lineNumber: 2, column: 1 });
                            }
                        }
                    }}
                />
            </EditorWrapper>
            {error && <ErrorBox theme={theme}>{error}</ErrorBox>}

            {showLint && lintDiagnostics.length > 0 && (
                <LintWrapper theme={theme}>
                    <LintHeader theme={theme}>
                        <LintTitle theme={theme}>ЛИНТЕР • {lintDiagnostics.length}</LintTitle>
                        <LintButton theme={theme} onClick={() => setShowLint(false)}>✕</LintButton>
                    </LintHeader>
                    <LintList theme={theme}>
                        {lintDiagnostics.map((d, i) => (
                            <LintItem key={i} theme={theme} severity={d.severity}>
                                <LintItemHeader>
                                    <LintBadge severity={d.severity} />
                                    <span style={{ flex: 1 }}>{d.message}</span>
                                    <LintMeta theme={theme}>стр {d.line}</LintMeta>
                                </LintItemHeader>
                                <LintActions>
                                    <LintButton theme={theme} onClick={() => jumpToLine(d.line)}>Перейти</LintButton>
                                    {d.suggestion && <LintButton theme={theme} onClick={() => applySuggestion(d)}>Исправить</LintButton>}
                                </LintActions>
                            </LintItem>
                        ))}
                    </LintList>
                </LintWrapper>
            )}

            {(isAnalyzing || (analysis && showAnalysis)) && (
                <AnalysisWrapper theme={theme}>
                    <AnalysisHeader theme={theme}>
                        <AnalysisTitle theme={theme}>🤖 Анализ ИИ</AnalysisTitle>
                        {isAnalyzing && (
                            <AnalyzingBox theme={theme}>
                                <Spinner />
                                Анализируем...
                            </AnalyzingBox>
                        )}
                        <HideAnalysisBtn theme={theme} onClick={() => setShowAnalysis(false)} title="Скрыть анализ">✕</HideAnalysisBtn>
                    </AnalysisHeader>
                    {analysis && (
                        <AnalysisContent theme={theme}>
                            {analysis.success ? (
                                <div>
                                    <AnalysisText>{analysis.analysis}</AnalysisText>
                                    <AnalysisFooter theme={theme}>Ответ от ИИ • {new Date(analysis.timestamp).toLocaleTimeString()}</AnalysisFooter>
                                </div>
                            ) : (
                                <AnalysisError>
                                    <span>⚠️</span>
                                    <span>Ошибка: {analysis.error}</span>
                                </AnalysisError>
                            )}
                        </AnalysisContent>
                    )}
                </AnalysisWrapper>
            )}
        </Panel>
    );
};

export default CodeGenerator;
